/**
 * EditConferimentoModal (#2 Opzione B) — modifica di un conferimento NON fatturato.
 * Edita i 5 campi di programmazione/note + i pesi reali (lordo/tara/netto).
 * Il netto NON è calcolato lato client per l'invio: si mostra solo un'anteprima
 * lordo − tara (come PesataModal); il netto definitivo lo determina il backend.
 * Se l'operatore digita un Peso netto manuale, quello viene inviato e fa fede.
 * Invia SOLO i campi modificati (dirty) → preserva la semantica manuale/calcolato.
 */
import { useEffect, useState } from 'react';
import { Pencil, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { updateConferimento, riallocaConferimento } from '../../api/conferimenti';
import type { ConferimentoDetail, UpdateConferimentoRequest } from '../../api/conferimenti';
import { listAreas } from '../../api/areas';
import type { Area } from '../../api/areas';
import { fmtKg } from '../../lib/saldo';

const num = (v: unknown) => (v === '' || v == null ? undefined : Number(v));
const schema = z.object({
  data_prevista: z.string().optional(),
  peso_stimato_kg: z.preprocess(num, z.number().positive().optional()),
  area_tentativa_id: z.string().optional(),
  numero_fir: z.string().optional(),
  note_programmazione: z.string().optional(),
  note: z.string().optional(),
  peso_lordo_kg: z.preprocess(num, z.number().nonnegative().optional()),
  tara_kg: z.preprocess(num, z.number().nonnegative().optional()),
  peso_netto_kg: z.preprocess(num, z.number().nonnegative().optional()),
  // Date reali (R5/F4): stringhe datetime-local; guardie di coerenza lato server.
  in_transito_at: z.string().optional(),
  attesa_compliance_at: z.string().optional(),
  compliance_confermata_at: z.string().optional(),
});
type FormData = z.infer<typeof schema>;

const inputCls = 'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';

interface Props {
  conferimento: ConferimentoDetail;
  onClose: () => void;
  onSaved: (updated: ConferimentoDetail) => void;
}

function str(v: string | number | null): string {
  return v == null ? '' : String(v);
}

/** TIMESTAMPTZ ISO → valore per input datetime-local (ora LOCALE, "YYYY-MM-DDTHH:mm").
 * Inverso in onSubmit: new Date(valore locale).toISOString() → ISO UTC per il BE. */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export default function EditConferimentoModal({ conferimento: c, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const [areas, setAreas] = useState<Area[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);

  // Gruppo F: per un conferimento in stoccaggio l'area è quella REALE (area_effettiva_id)
  // e si cambia con un'azione dedicata (riallocazione), separata dal Salva.
  const inStoccaggio = c.status === 'in_stoccaggio';
  const [reallocArea, setReallocArea] = useState<string>(str(c.area_effettiva_id));
  const [reallocBusy, setReallocBusy] = useState(false);
  const [reallocError, setReallocError] = useState<string | null>(null);
  const [confirmRealloc, setConfirmRealloc] = useState(false);
  const [showOverride, setShowOverride] = useState(false);

  useEffect(() => { listAreas().then(setAreas).catch(() => {}); }, []);

  async function doRialloca(override: boolean) {
    setReallocError(null);
    setReallocBusy(true);
    try {
      const updated = await riallocaConferimento(c.id, { area_effettiva_id: reallocArea, override_capacita: override });
      onSaved(updated);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      // 422 con override_richiesto → dialog di conferma override (come lo Stoccaggio).
      if (detail && typeof detail === 'object' && (detail as { override_richiesto?: boolean }).override_richiesto) {
        setConfirmRealloc(false);
        setShowOverride(true);
        return;
      }
      setReallocError(typeof detail === 'string' ? detail : t('conferimenti.edit_modal.errore_riallocazione'));
    } finally {
      setReallocBusy(false);
    }
  }

  const { register, handleSubmit, watch, formState: { errors, isSubmitting, dirtyFields } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      data_prevista: str(c.data_prevista),
      peso_stimato_kg: str(c.peso_stimato_kg),
      area_tentativa_id: str(c.area_tentativa_id),
      numero_fir: str(c.numero_fir),
      note_programmazione: str(c.note_programmazione),
      note: str(c.note),
      peso_lordo_kg: str(c.peso_lordo_kg),
      tara_kg: str(c.tara_kg),
      peso_netto_kg: str(c.peso_netto_kg),
      in_transito_at: toLocalInput(c.in_transito_at),
      attesa_compliance_at: toLocalInput(c.attesa_compliance_at),
      compliance_confermata_at: toLocalInput(c.compliance_confermata_at),
    },
  });

  // eslint-disable-next-line react-hooks/incompatible-library -- RHF watch(): il progetto non usa il React Compiler
  const lordo = watch('peso_lordo_kg');
  const tara = watch('tara_kg');
  const nettoAnteprima =
    lordo !== '' && lordo != null && tara !== '' && tara != null && !Number.isNaN(Number(lordo)) && !Number.isNaN(Number(tara))
      ? Number(lordo) - Number(tara)
      : null;

  async function onSubmit(data: FormData) {
    setServerError(null);
    // Solo i campi effettivamente modificati (dirty) finiscono nel body: così un
    // peso_netto non toccato NON viene inviato (il BE ricalcola da lordo − tara).
    const body: UpdateConferimentoRequest = {};
    if (dirtyFields.data_prevista && data.data_prevista) body.data_prevista = data.data_prevista;
    if (dirtyFields.peso_stimato_kg && data.peso_stimato_kg != null) body.peso_stimato_kg = data.peso_stimato_kg;
    if (dirtyFields.numero_fir) body.numero_fir = data.numero_fir ?? '';
    // In stoccaggio l'area NON passa mai da update_conferimento (è gestita dalla riallocazione).
    if (!inStoccaggio && dirtyFields.area_tentativa_id && data.area_tentativa_id) body.area_tentativa_id = data.area_tentativa_id;
    if (dirtyFields.note_programmazione) body.note_programmazione = data.note_programmazione ?? '';
    if (dirtyFields.note) body.note = data.note ?? '';
    if (dirtyFields.peso_lordo_kg && data.peso_lordo_kg != null) body.peso_lordo_kg = data.peso_lordo_kg;
    if (dirtyFields.tara_kg && data.tara_kg != null) body.tara_kg = data.tara_kg;
    if (dirtyFields.peso_netto_kg && data.peso_netto_kg != null) body.peso_netto_kg = data.peso_netto_kg;
    // Date reali: datetime-local (ora locale) → ISO UTC. Campo svuotato = non inviato.
    if (dirtyFields.in_transito_at && data.in_transito_at) body.in_transito_at = new Date(data.in_transito_at).toISOString();
    if (dirtyFields.attesa_compliance_at && data.attesa_compliance_at) body.attesa_compliance_at = new Date(data.attesa_compliance_at).toISOString();
    if (dirtyFields.compliance_confermata_at && data.compliance_confermata_at) body.compliance_confermata_at = new Date(data.compliance_confermata_at).toISOString();

    try {
      const updated = await updateConferimento(c.id, body);
      onSaved(updated);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setServerError(typeof detail === 'string' ? detail : t('conferimenti.edit_modal.errore_salvataggio'));
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[95vh] overflow-y-auto" data-testid="edit-conferimento-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-mid sticky top-0 bg-white">
          <h2 className="text-base font-semibold text-ink-dark flex items-center gap-2">
            <Pencil size={16} className="text-sage" /> {t('conferimenti.edit_modal.titolo')}
          </h2>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink-dark"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit((d) => onSubmit(d as FormData))}>
          <div className="px-6 py-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-ink-soft mb-1">{t('conferimenti.edit_modal.data_prevista')}</label>
                <input type="date" className={inputCls} {...register('data_prevista')} />
              </div>
              <div>
                <label className="block text-xs font-medium text-ink-soft mb-1">{t('conferimenti.edit_modal.peso_stimato')}</label>
                <input type="number" step="0.01" className={inputCls} data-testid="edit-peso-stimato" {...register('peso_stimato_kg')} />
                {errors.peso_stimato_kg && <p className="text-xs text-accent-danger mt-1">{t('conferimenti.edit_modal.val_peso_stimato')}</p>}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('conferimenti.edit_modal.numero_fir')}</label>
              <input type="text" className={inputCls} data-testid="edit-numero-fir" {...register('numero_fir')} />
            </div>

            {!inStoccaggio ? (
              <div>
                <label className="block text-xs font-medium text-ink-soft mb-1">{t('conferimenti.edit_modal.area_tentativa')}</label>
                <select className={`${inputCls} cursor-pointer`} data-testid="edit-area" {...register('area_tentativa_id')}>
                  <option value="">{t('conferimenti.edit_modal.area_nessuna')}</option>
                  {areas.map((a) => <option key={a.id} value={a.id}>{a.codice} — {a.descrizione}</option>)}
                </select>
              </div>
            ) : (
              /* Area REALE di stoccaggio: cambio = riallocazione (azione dedicata). */
              <div className="border border-stone-mid rounded-lg p-3 bg-stone-bg/40" data-testid="realloc-block">
                <label className="block text-xs font-semibold text-ink-soft mb-1">{t('conferimenti.edit_modal.area_stoccaggio')}</label>
                <select className={`${inputCls} cursor-pointer`} data-testid="realloc-area"
                  value={reallocArea} onChange={(e) => { setReallocArea(e.target.value); setConfirmRealloc(false); setReallocError(null); }}>
                  {areas.map((a) => <option key={a.id} value={a.id}>{a.codice} — {a.descrizione}</option>)}
                </select>
                {!confirmRealloc ? (
                  <button type="button" data-testid="btn-sposta-area" disabled={reallocBusy || !reallocArea || reallocArea === str(c.area_effettiva_id)}
                    onClick={() => { setReallocError(null); setConfirmRealloc(true); }}
                    className="mt-2 px-3 py-1.5 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    {t('conferimenti.edit_modal.sposta_area')}
                  </button>
                ) : (
                  <div className="mt-2" data-testid="realloc-confirm">
                    <p className="text-xs text-ink-mid mb-1.5">{t('conferimenti.edit_modal.sposta_conferma')}</p>
                    <div className="flex gap-2">
                      <button type="button" data-testid="btn-conferma-sposta" disabled={reallocBusy} onClick={() => void doRialloca(false)}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark disabled:opacity-60">{t('common.azioni.conferma')}</button>
                      <button type="button" disabled={reallocBusy} onClick={() => setConfirmRealloc(false)}
                        className="px-3 py-1.5 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white disabled:opacity-50">{t('common.azioni.annulla')}</button>
                    </div>
                  </div>
                )}
                {showOverride && (
                  <div className="mt-2 px-3 py-2 bg-accent-warning-bg border border-[#FDE68A] rounded" data-testid="realloc-override">
                    <p className="text-xs text-ink-mid mb-1.5">{t('conferimenti.edit_modal.sposta_override')}</p>
                    <div className="flex gap-2">
                      <button type="button" data-testid="btn-conferma-override" disabled={reallocBusy} onClick={() => void doRialloca(true)}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark disabled:opacity-60">{t('conferimenti.edit_modal.sposta_override_conferma')}</button>
                      <button type="button" disabled={reallocBusy} onClick={() => setShowOverride(false)}
                        className="px-3 py-1.5 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white disabled:opacity-50">{t('common.azioni.annulla')}</button>
                    </div>
                  </div>
                )}
                {reallocError && <p className="text-xs text-accent-danger mt-1.5" data-testid="realloc-error">{reallocError}</p>}
              </div>
            )}

            {/* Pesi reali */}
            <div className="border-t border-stone-mid pt-4">
              <p className="text-xs font-semibold text-ink-soft mb-2">{t('conferimenti.edit_modal.sez_pesi')}</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-ink-soft mb-1">{t('conferimenti.edit_modal.peso_lordo')}</label>
                  <input type="number" step="0.01" className={inputCls} data-testid="edit-peso-lordo" {...register('peso_lordo_kg')} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-ink-soft mb-1">{t('conferimenti.edit_modal.tara')}</label>
                  <input type="number" step="0.01" className={inputCls} data-testid="edit-tara" {...register('tara_kg')} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-ink-soft mb-1">{t('conferimenti.edit_modal.peso_netto')}</label>
                  <input type="number" step="0.01" className={inputCls} data-testid="edit-peso-netto" {...register('peso_netto_kg')} />
                </div>
              </div>
              {nettoAnteprima != null && (
                <p className="text-xs text-ink-mid mt-2" data-testid="edit-netto-anteprima">
                  {t('conferimenti.edit_modal.netto_anteprima', { n: fmtKg(nettoAnteprima) })}
                </p>
              )}
              <p className="text-[11px] text-ink-faint mt-1">{t('conferimenti.edit_modal.netto_nota')}</p>
            </div>

            {/* Date reali (R5/F4): correzione a posteriori — un campo per fase AVVENUTA
                (coerente con la guardia BE: mai anticipare una fase non ancora avvenuta). */}
            {(c.in_transito_at || c.attesa_compliance_at || c.compliance_confermata_at) && (
              <div className="border-t border-stone-mid pt-4" data-testid="sez-date-reali">
                <p className="text-xs font-semibold text-ink-soft mb-2">{t('conferimenti.edit_modal.sez_date_reali')}</p>
                <div className="grid grid-cols-2 gap-3">
                  {c.in_transito_at && (
                    <div>
                      <label className="block text-xs font-medium text-ink-soft mb-1">{t('conferimenti.edit_modal.data_arrivo')}</label>
                      <input type="datetime-local" className={inputCls} data-testid="edit-data-arrivo" {...register('in_transito_at')} />
                    </div>
                  )}
                  {c.attesa_compliance_at && (
                    <div>
                      <label className="block text-xs font-medium text-ink-soft mb-1">{t('conferimenti.edit_modal.data_pesata')}</label>
                      <input type="datetime-local" className={inputCls} data-testid="edit-data-pesata" {...register('attesa_compliance_at')} />
                    </div>
                  )}
                  {c.compliance_confermata_at && (
                    <div>
                      <label className="block text-xs font-medium text-ink-soft mb-1">{t('conferimenti.edit_modal.data_stoccaggio')}</label>
                      <input type="datetime-local" className={inputCls} data-testid="edit-data-stoccaggio" {...register('compliance_confermata_at')} />
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-ink-faint mt-1">{t('conferimenti.edit_modal.date_reali_nota')}</p>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('conferimenti.edit_modal.note_programmazione')}</label>
              <textarea rows={2} className={inputCls} {...register('note_programmazione')} />
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('conferimenti.edit_modal.note')}</label>
              <textarea rows={2} className={inputCls} {...register('note')} />
            </div>

            {serverError && (
              <div className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger" data-testid="edit-error">{serverError}</div>
            )}
          </div>
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-stone-mid bg-stone-bg sticky bottom-0">
            <button type="button" onClick={onClose} disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50">{t('common.azioni.annulla')}</button>
            <button type="submit" disabled={isSubmitting} data-testid="edit-salva" className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-60">
              {isSubmitting ? t('conferimenti.edit_modal.salvataggio') : t('conferimenti.edit_modal.salva')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
